import {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Dispatch,
} from 'react';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { mapSubscriberEditDto } from 'theSource/features/Profile/ProfileDetailsEditPage/utils';
import { useConfigurationQuery } from 'theSource/queries';
import {
  ErrorResponseDto,
  FormFieldsCategory,
  FormField,
  FieldValueDto,
  ResponseDto,
  SubscriberEditRequestDto,
} from 'theSource/types';
import {
  convertApiDateToDateTime,
  getInitialValues,
  queryClient,
  useApiMutation,
} from 'theSource/utils';
import { formatDateString } from 'enrollment/utils/FieldUtils/fieldUtils';
import {
  GET_PROFILE_QUERY_KEY,
  UPDATE_PROFILE_QUERY_KEY,
} from 'enrollment/queries';
import { Spacer } from 'theSource/components';
import { PageContentHeaderDisplay } from 'enrollment/components';
import { FormElement } from 'enrollment/components/Shared/FormElement/';
import { selectEnrollmentOrgPlanId } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { RequiredLegend } from 'enrollment/features/Shared/RequiredLegend/RequiredLegend';
import autocompleteValues from 'theSource/utils/fieldUtils/autocomplete.json';
import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';
import { profileformFields } from '../ProfileDetailsPage/ProfileHelper';

export interface ProfileDetailsEditPageProps {
  categories: FormFieldsCategory[];
  setDrawerOpen: Function;
  toggleIsFormValid: (value: boolean) => void;
  setIsSubmitting: Dispatch<React.SetStateAction<boolean>>;
  setIsToastVisible?: Dispatch<React.SetStateAction<boolean>>;
  setToastMessage?: Dispatch<React.SetStateAction<string>>;
  setAwaitSuccess?: Dispatch<React.SetStateAction<boolean>>;
}

export const ProfileDetailsEditPage = forwardRef(
  (
    {
      categories,
      setDrawerOpen,
      toggleIsFormValid,
      setIsSubmitting,
      setAwaitSuccess,
    }: ProfileDetailsEditPageProps,
    ref
  ) => {
    const initialValues = useMemo(
      () => getInitialValues(categories),
      [categories]
    );

    const [changedFields, setChangedFields] = useState<{
      [key: string]: boolean;
    }>({});
    const [fieldsValidity, setFieldsValidity] = useState<{
      [key: string]: boolean;
    }>({});

    const [fieldValues, setFieldValues] = useState<{ [key: string]: any }>(getInitialValues(categories));

    const setSaveDisabled = useState(true)[1];
    const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);

    const { configuration } = useConfigurationQuery();
    const lumosApiHost = configuration?.apiHost;

    const toast = useToastDialog();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const setDialogRef = useCallback((el: HTMLDivElement) => {
      containerRef.current = el;
    }, []);

    const { mutate: updateSubscriber } = useApiMutation<
      ResponseDto,
      ErrorResponseDto,
      SubscriberEditRequestDto
    >({
      queryKey: UPDATE_PROFILE_QUERY_KEY,
      method: 'put',
      endpoint: `${lumosApiHost}/v2/self_service/employees/profile`,
      onSuccess: () => {
        queryClient.invalidateQueries(GET_PROFILE_QUERY_KEY);

        setAwaitSuccess?.(false);

        document.addEventListener('psSubmitDialog', (e) => {
          setIsSubmitting(false);
          if ((e as CustomEvent).detail === 'profile_edit_info') {
            setAwaitSuccess?.(true);
            setDrawerOpen(false);
          }
        });
      },
      onError: (error) => {
        setIsSubmitting(false);
        if (containerRef.current) {
          toast.showToast({
            message: error.response?.data?.details || error.message || 'An unknown error occurred',
            status: 'danger',
            closeIcon: true,
            container: containerRef.current,
          });
        }
      },
      showSuccessSnackbar: false,
      showErrorSnackbar: false,
    });

    useEffect(() => {
      toggleIsFormValid(false);
    }, [toggleIsFormValid]);

    useEffect(() => {
      const hasChangedFields = Object.values(changedFields).some(
        (changed) => changed
      );
      const allValid = Object.values(fieldsValidity).every((valid) => valid);

      const canSave = hasChangedFields && allValid;
      setSaveDisabled(!canSave);
      toggleIsFormValid(canSave);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changedFields, fieldsValidity, toggleIsFormValid]);

    const prepareRequestObj = () => {
      const requestObj = Object.keys(fieldValues).reduce(
        (obj, fieldId) => {
          if (fieldValues[fieldId] !== undefined && fieldValues[fieldId] !== null) {
            const value = fieldValues[fieldId];

            // Convert DateTime objects to ISO strings before submission
            if (value instanceof DateTime) {
              obj[fieldId] = value.toISODate(); // Use date-only format
            } else if (typeof value === 'boolean') {
              obj[fieldId] = value; // Keep boolean as-is
            } else {
              obj[fieldId] = value?.toString(); // Ensure text/select fields are submitted as strings
            }
          }
          return obj;
        },
        {} as { [key: string]: any }
      );

      return requestObj;
    };

    const handleSubmit = () => {
      const requestObj = prepareRequestObj();

      const mappedRequestObj = mapSubscriberEditDto(
        categories ?? [],
        requestObj
      );

      updateSubscriber(mappedRequestObj);
    };

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    const handleValidityChange = (isValid: boolean, fieldId: string) => {
      setFieldsValidity((prev) => {
        if (prev[fieldId] !== isValid) {
          return { ...prev, [fieldId]: isValid };
        }
        return prev;
      });
    };

    const handleFieldChange = (
      field: FormField,
      input: HTMLInputElement,
    ) => {
      const fieldId = field.fieldName;
      const dataType = field.dataType.toUpperCase();
      const newValue = input.type === 'checkbox' ? input.checked : input.value;
      const initialValue = initialValues[fieldId];
      let formattedValue = newValue as FieldValueDto;
      let hasChanged;

      // Handle data type-specific formatting
      if (dataType === 'DATE') {
        formattedValue = newValue
          ? convertApiDateToDateTime(formatDateString(String(newValue)))
          : null;

        // Normalize for comparison
        const normalizedInitialValue = initialValue
          ? DateTime.fromISO(String(initialValue)).toISODate()
          : null;
        const normalizedFormattedValue = formattedValue
          ? DateTime.fromISO(String(formattedValue)).toISODate()
          : null;

        hasChanged = normalizedInitialValue !== normalizedFormattedValue;
      } else if (dataType === 'BOOLEAN') {
        formattedValue = newValue === 'true' || newValue === true; // Ensure boolean handling
        hasChanged = initialValue !== formattedValue;
      } else if (dataType === 'NUMBER') {
        formattedValue = Number(newValue); // Ensure number type
        hasChanged = initialValue !== formattedValue;
      } else {
        formattedValue = newValue?.toString().trim(); // Handle text and select as strings
        hasChanged = initialValue !== formattedValue;
      }

      setChangedFields((prev) => ({
        ...prev,
        [fieldId]: hasChanged,
      }));

      setFieldValues((prev) => ({
        ...prev,
        [fieldId]: formattedValue,
      }));

      const fieldElement = document.getElementById(fieldId) as HTMLInputElement;
      if (fieldElement) {
        const isValid = fieldElement.checkValidity();
        setFieldsValidity((prev) => ({
          ...prev,
          [fieldId]: isValid,
        }));
      }
    };

    const getFormFieldType = (
      dataType: any,
      options: any,
      dataFieldName: any
    ) => {
      switch (dataType.toLowerCase()) {
        case 'text':
          if (options && options.length > 0) {
            return 'select';
          } else if (dataFieldName.toLowerCase().includes('phone')) {
            return 'tel';
          } else if (dataFieldName.toLowerCase() === 'email') {
            return 'email';
          } else {
            return 'text';
          }
        case 'number':
          if (options && options.length > 0) {
            return 'select';
          } else {
            return 'number';
          }
        case 'date':
          return 'date';
        case 'boolean':
          return 'checkbox';
        default:
          return 'text';
      }
    };

    const loadInitialValues = (dataValue: any, dataFieldName: any) => {
      let value;

      // Handle DateTime objects directly
      if (dataValue instanceof DateTime) {
        value = dataValue.toISODate(); // Convert DateTime to ISO date string (YYYY-MM-DD)
      } else {
        value = dataValue?.value ?? dataValue; // Extract 'value' if present, fallback to raw dataValue
      }

      // Handle date fields with string format
      if (
        value &&
        typeof value === 'string' &&
        dataFieldName.toLowerCase().includes('date')
      ) {
        // Replace slashes with hyphens for proper parsing
        const formattedDate = value.replace(/\//g, '-');

        const dt = DateTime.fromISO(formattedDate);

        return dt.toISODate(); // This will return 'YYYY-MM-DD'
      }

      switch (dataFieldName) {
        case 'country':
          return value || 'United States';
        default:
          return value;
      }
    };

    const showIconOptional = (dataFieldName: any) => {
      if (dataFieldName.toLowerCase().includes('salary')) {
        return 'currency-dollar';
      }
    };

    return (
      <>
        <PageContentHeaderDisplay
          pageContentLookupCode="enroll_personal"
          displayPageHeader={false}
          orgPlanYearId={orgPlanYearId}
        />
        <Spacer height={5} />
        <div ref={setDialogRef}>
          <RequiredLegend />
          {categories.map((category: FormFieldsCategory, categoryIndex: number) => {
            const filteredFields = category.fields.filter(
              (field: any) => field.fieldName && Object.prototype.hasOwnProperty.call(profileformFields, field.fieldName)
            );

            return (
              <div className="d-flex flex-column mb-5" key={categoryIndex}>
                <h2 className="headline-3">{category.name}</h2>
                {filteredFields.map((field: FormField, fieldIndex: number) => {
                  const isFieldDisabled = field.editable === false;

                  // Dynamically fetch autocomplete value based on the field's state
                  const autocompleteValue = isFieldDisabled
                    ? 'off' // Set autocomplete to "off" if the field is disabled
                    : autocompleteValues[
                    field.fieldName as keyof typeof autocompleteValues
                    ] || 'on';

                  return (
                    <div className="py-2" key={fieldIndex}>
                      <FormElement
                        formField={getFormFieldType(
                          field.dataType,
                          field.options,
                          field.fieldName
                        )}
                        inputId={field.fieldName}
                        label={field.label}
                        name={field.fieldName}
                        options={field.options}
                        required={field.required}
                        value={loadInitialValues(field.value, field.fieldName)}
                        {...field.dataType.toLowerCase() === 'boolean' && {
                          checked: Boolean(field.value),
                        }}
                        icon={showIconOptional(field.fieldName)}
                        disabled={field.editable === false} // Prevent user interaction
                        autocomplete={autocompleteValue} // Dynamically set autocomplete value
                        onValidityChange={
                          field.required
                            ? (isValid) =>
                              handleValidityChange(isValid, field.fieldName)
                            : undefined
                        }
                        onInput={(e: any) => {
                          handleFieldChange(
                            field,
                            e.target as HTMLInputElement,
                          );
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </>
    );
  }
);
